import { useCallback, useEffect, useRef, useState } from 'react';
import { websocketService } from '../services/websocket';
import { useAuthStore } from '../stores/authStore';

interface WebSocketMessage {
  event: string;
  data: unknown;
}

interface UseWebSocketReturn {
  isConnected: boolean;
  lastMessage: WebSocketMessage | null;
  send: (event: string, data: unknown) => void;
}

export function useWebSocket(): UseWebSocketReturn {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const { accessToken, isAuthenticated } = useAuthStore();
  const hasConnectedRef = useRef(false);

  // Auto-connect when authenticated
  useEffect(() => {
    if (isAuthenticated && accessToken && !hasConnectedRef.current) {
      hasConnectedRef.current = true;
      websocketService.connect(accessToken);
    }

    // Auto-disconnect on logout
    if (!isAuthenticated && hasConnectedRef.current) {
      hasConnectedRef.current = false;
      websocketService.disconnect();
      setIsConnected(false);
      setLastMessage(null);
    }
    // NO cleanup function - singleton should persist
  }, [isAuthenticated, accessToken]);

  // Set up event listeners
  useEffect(() => {
    const unsubscribeMessage = websocketService.onMessage((event, data) => {
      setLastMessage({ event, data });
    });

    const unsubscribeConnect = websocketService.onConnect(() => {
      setIsConnected(true);
    });

    const unsubscribeDisconnect = websocketService.onDisconnect(() => {
      setIsConnected(false);
    });

    // Check initial connection state
    setIsConnected(websocketService.isConnected());

    return () => {
      unsubscribeMessage();
      unsubscribeConnect();
      unsubscribeDisconnect();
    };
  }, []);

  const send = useCallback((event: string, data: unknown): void => {
    websocketService.send(event, data);
  }, []);

  return {
    isConnected,
    lastMessage,
    send,
  };
}
